'use client'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Container, Grid, Stack, styled, Typography } from '@mui/material'

import { ContentStack } from '@/components/ContentStack/ContentStack'
import { useTranslations } from '@/hooks/useTranslations'
import { colors } from '@/themes/commerce/colors'

import type React from 'react'
import type { Footer as SanityFooter } from '@/gen/sanity/schema'

interface SiteFooterProps {
  footer?: SanityFooter
}

const Item = styled(Box)(({ theme }) => ({
  ...theme.typography.body1,
  padding: theme.spacing(1),
  color: theme.palette.text.light,
  '& .MuiLink-root': {
    ...theme.typography.body2,
    color: theme.palette.text.light,
    textDecoration: 'none',
    position: 'relative',

    [theme.breakpoints.up('sm')]: {
      ...theme.typography.body1,
    },
  },
  '& .MuiStack-root': {
    marginTop: 0,
  },
  '& .MuiTypography-body1': {
    marginTop: 0,
  },
  '& .MuiTypography-body2': {
    marginTop: 0,
    ...theme.typography.body2,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.body1,
    },
  },
}))

const LogoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',

  [theme.breakpoints.up('lg')]: {
    marginLeft: '56px',
  },

  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column-reverse',
    gridGap: 24,
  },
}))

const StyledStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',

  [theme.breakpoints.down('lg')]: {
    justifyContent: 'center',
  },
}))

export const Footer: React.FC<SiteFooterProps> = ({ footer }) => {
  const { language } = useTranslations()

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        backgroundColor: colors.background.section.darker,
        [theme.breakpoints.up('md')]: {
          background: `linear-gradient(
            to right,
            ${colors.background.section.dark} 0%,
            ${colors.background.section.dark} 50%,
            ${colors.background.section.darker} 50%,
            ${colors.background.section.darker} 100%
          )`,
        },
      })}
    >
      <Container>
        <Grid container>
          <Grid
            item
            xs={12}
            md={5}
            lg={4}
            sx={{
              backgroundColor: colors.background.section.dark,
              paddingBottom: 5,
            }}
          >
            <Item
              sx={(theme) => ({
                textAlign: 'left',
                marginTop: 4,
                [theme.breakpoints.down('lg')]: {
                  paddingLeft: 7,
                  paddingRight: 2,
                  marginTop: 1,
                },
                [theme.breakpoints.down('sm')]: {
                  paddingLeft: 1,
                },
              })}
            >
              {footer && !!footer.legal?.items?.length && <ContentStack content={footer.legal} />}
            </Item>
            {footer?.columns?.map(
              (col, index) =>
                index < 2 && (
                  <div key={`footer-${index}`}>
                    <Item
                      sx={(theme) => ({
                        textAlign: 'left',
                        marginTop: 2,
                        [theme.breakpoints.down('lg')]: {
                          paddingLeft: 7,
                          paddingRight: 2,
                          marginTop: 1,
                        },
                      })}
                    >
                      <Typography variant="h4">{col?.title?.[language] ?? col?.internalName}</Typography>
                    </Item>
                    <Item
                      sx={(theme) => ({
                        [theme.breakpoints.down('lg')]: {
                          paddingLeft: 7,
                          paddingRight: 2,
                          marginTop: 1,
                        },
                        [theme.breakpoints.down('sm')]: {
                          paddingLeft: 1,
                        },
                      })}
                    >
                      <ContentStack content={col?.content} />
                    </Item>
                  </div>
                )
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
            lg={8}
            sx={{
              backgroundColor: colors.background.section.darker,
              paddingBottom: 5,
            }}
          >
            <Grid container sx={{ paddingLeft: 7 }}>
              {footer?.columns?.map(
                (col, index) =>
                  index > 1 && (
                    <Grid key={col._key} item xs={12} md={6} lg={4}>
                      <Item
                        sx={(theme) => ({
                          textAlign: 'left',
                          marginTop: 6,
                          [theme.breakpoints.down('lg')]: {
                            marginTop: 1,
                          },
                        })}
                      >
                        <Typography variant="h4">{col.title?.[language] ?? col.internalName}</Typography>
                      </Item>
                      <Item>
                        <ContentStack
                          content={col.content}
                          linkIcon={<ChevronRightIcon color="inherit" fontSize="inherit" />}
                        />
                      </Item>
                    </Grid>
                  )
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid
            item
            xs={12}
            md={5}
            lg={4}
            sx={(theme) => ({
              paddingBottom: 5,
              display: 'none',
              [theme.breakpoints.up('md')]: {
                backgroundColor: colors.background.section.dark,
                display: 'block',
              },
            })}
          />
          <Grid
            item
            xs={12}
            md={7}
            lg={8}
            sx={{
              backgroundColor: colors.background.section.darker,
              paddingBottom: 5,
            }}
          >
            <LogoContainer>
              <StyledStack justifyContent="center">
                {footer && !!footer.bottom?.items?.length && (
                  <Item>
                    <ContentStack
                      content={footer.bottom}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 0, sm: 2 }}
                    />
                  </Item>
                )}
              </StyledStack>
              <StyledStack justifyContent="center">
                {footer && !!footer.bottomRight?.items?.length && (
                  <Item>
                    <ContentStack content={footer.bottomRight} alignItems="center" justifyContent="space-between" />
                  </Item>
                )}
              </StyledStack>
            </LogoContainer>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}
